import { router } from "@inertiajs/react"
import { Check, X } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { useOnline } from "@/hooks/use-online"
import { cn } from "@/lib/utils"
import type { CandidateActions } from "@/types/candidates"

/**
 * OKS-998 — should the sticky decision bar mount at all?
 *
 * Exported and used in exactly TWO places: this component's own early
 * return, and `pages/Brand/Candidates/Show.tsx`, which needs the same
 * answer to decide whether the page container gets its bottom padding.
 *
 * 🔴 The export exists so those two cannot drift. BRIEF AC5 is falsified
 * by "a `new` candidate's page ending in dead space" — i.e. padding
 * present while the bar is absent — and the only structural way to make
 * that unrepresentable is one expression with one caller each side.
 * If you inline this predicate at either site, AC5 becomes a thing a
 * test has to catch rather than a thing that cannot happen.
 */
export function shouldRenderStickyDecisionBar(
  actions: CandidateActions,
): boolean {
  return actions.approve_url !== null || actions.reject_url !== null
}

export interface CandidateStickyDecisionBarProps {
  /**
   * The OKS-19 transition-URL surface. Only `approve_url` and
   * `reject_url` are read — the secondary verbs stay in the header
   * `CandidateActionBar` (CTPO decision, BRIEF §2).
   */
  actions: CandidateActions
  /**
   * Opens the page's SINGLE `RejectionReasonPicker`, which is owned by
   * `CandidateActionBar` and driven from `Show.tsx` (BRIEF D2).
   *
   * ⚠️ This bar deliberately does NOT own a reject modal. A second
   * `RejectionReasonPicker` on the page would double
   * `candidate-detail-reject-confirm` and
   * `candidate-detail-reject-reason-code`, breaking Playwright strict
   * mode and making every gate assertion ambiguous about which copy it
   * proved.
   */
  onRejectClick: () => void
  className?: string
}

/**
 * OKS-998 — Sticky Approve/Reject decision bar for the candidate detail
 * page.
 *
 * **Why it exists.** A reviewer scrolls a candidate to read the profile
 * and both decisions scroll away: Reject lives in the page header, and
 * OKS-673 moved Approve into the mid-page Invitations panel. This bar
 * pins the two decisions to the viewport at every scroll position. It
 * DUPLICATES rather than relocates — the panel's Approve
 * (`candidate-detail-approve`) and the header's Reject
 * (`candidate-detail-reject`) both stay exactly where they are.
 *
 * **Shape (BRIEF D1).** Floating pill, `fixed` at EVERY breakpoint,
 * copied from `candidate-bulk-action-bar.tsx`. Deliberately NOT the
 * `CrewMemberActionBar` shape: that one carries `sm:static` alongside
 * its `fixed bottom-0`, so it is pinned only below 640px and scrolls
 * away on desktop — which is the reported complaint, not a fix for it.
 *
 * **No mount animation**, unlike the bulk bar it copies. That bar
 * animates because it APPEARS on selection; this one is present at
 * first paint, so a slide-in would fire on every page load as noise.
 *
 * **`z-40` (BRIEF §3).** The constraint is `< 50` — every overlay in
 * `components/ui/` (dialog, alert-dialog, drawer, sheet) puts `z-50` on
 * both overlay and content and portals to `document.body`, so the bar
 * is under all of them by construction rather than by tuning. Below
 * 768px the reject Drawer slides up from `bottom-0` directly over this
 * bar; that is the desired reading, not a collision.
 *
 * **Visibility is the page's standing hide-when-null rule.** The bar
 * mounts iff at least one of `approve_url` / `reject_url` is non-null,
 * and each button renders iff its own URL is non-null. No status logic
 * on the client — and no *setting* logic either; see below.
 *
 * `CandidatesController#eligible_for?` AND-gates THREE conditions, and
 * the rendered matrix is the product of all three:
 *   1. `Candidate::TRANSITIONS[event][:from]` — `approve` from
 *      `reviewed` only; `reject` from `reviewed`,
 *      `pending_brand_validation`, `approved`, `contacted`, `invited`.
 *   2. `Candidates::EventEligibility` — the brand's
 *      `candidate_validation` setting. `SETTING_OFFERS["always"]` is
 *      `%w[request_brand_validation]`, i.e. NO `approve`.
 *   3. `CandidatePolicy#transition?` — the actor.
 *
 * So: both buttons at `reviewed` on a `never`/`on_request` brand;
 * **Reject alone at `reviewed` on an `always` brand** — correct
 * behaviour, not a missing button; Reject alone at
 * `pending_brand_validation`, `approved`, `contacted`, `invited`;
 * absent at `new`, `joined`, `rejected`, `blocklisted`, and absent
 * while the candidate is soft-removed (the server nulls every
 * transition URL).
 *
 * 🔴 Gate 2 costs this component nothing, and that is structural: it
 * reads `actions.approve_url` and never the setting. It has no access
 * to `candidate_validation` and MUST NOT acquire any — a third gate
 * upstream is invisible to a component whose only input is URL
 * nullability, which is exactly why the rule is worth keeping.
 *
 * 🔴 **The `new` case is a known accepted gap, not a bug.** At `new`
 * the only forward action is Mark as reviewed, which OKS-673 had
 * already moved out of the header into the mid-page panel — so at the
 * arrival state of every imported candidate there is still no pinned
 * forward action, and the reported complaint is untouched there. CTPO
 * wrote "Approve + Reject only"; widening the bar to `mark_reviewed_url`
 * as a third hide-when-null entry is a one-line change if that call is
 * revisited. Recorded in BRIEF §8 OQ8. Do not "fix" it silently.
 *
 * **No focus steal on mount.** The bulk bar focuses its Clear button
 * because selection is a deliberate user action; here mount happens on
 * page load, and stealing focus on load is an a11y regression. The bar
 * is also the LAST child of the page container, so it is last in
 * reading and tab order despite being visually pinned.
 *
 * **Testids** — `candidate-detail-sticky-bar` (stable root, asserted
 * `toHaveCount(0)` by the absence arms), `candidate-detail-sticky-approve`,
 * `candidate-detail-sticky-reject`. These are deliberately distinct from
 * the panel's and the header's: four Playwright spec files reach
 * `candidate-detail-approve` at PAGE scope, two of them asserting
 * `toHaveCount(0)`, so a duplicated testid would red the count
 * assertions and trip strict mode on the clicks.
 */
export function CandidateStickyDecisionBar({
  actions,
  onRejectClick,
  className,
}: CandidateStickyDecisionBarProps) {
  const { t } = useTranslation()
  const online = useOnline()
  // Only Approve POSTs from this bar — Reject opens the shared picker
  // and the POST happens there — so a single flag is the whole
  // in-flight surface. Both buttons disable while it is set, so a
  // reviewer cannot fire Approve and Reject into the same navigation.
  const [approveInFlight, setApproveInFlight] = useState(false)

  if (!shouldRenderStickyDecisionBar(actions)) return null

  const disabled = approveInFlight || !online

  function handleApprove() {
    const url = actions.approve_url
    if (!url || disabled) return
    setApproveInFlight(true)
    // Nested strong-params wire shape, locked BE-side as
    // `params.require(:transition).permit(:event, :reason)`. Byte-identical
    // to `InvitesPanelEmpty`'s advance POST and to
    // `CandidateActionBar#dispatchPost` — posting the flat `{event}` would
    // 400 on the missing `:transition` root.
    router.post(
      url,
      { transition: { event: "approve" } },
      {
        preserveScroll: true,
        onFinish: () => setApproveInFlight(false),
      },
    )
  }

  return (
    <div
      data-testid="candidate-detail-sticky-bar"
      role="region"
      // « Refuser » now appears twice on the page (header + here), and a
      // named region is what lets a screen-reader user tell them apart.
      aria-label={t("candidates.detail.actions.sticky_bar_aria")}
      className={cn(
        "fixed bottom-0 left-1/2 z-40 -translate-x-1/2",
        // Proven at 375px by the list page's bulk bar. Capped at 480px
        // rather than the bulk bar's 720px: two buttons and nothing else
        // would either stretch absurdly or float in white at that width.
        "w-[min(calc(100vw-32px),480px)]",
        "bg-background rounded-t-md border border-b-0 shadow-lg",
        "flex items-center gap-3 px-4 py-3",
        // iOS safe-area: float above the home indicator. The `max()` keeps
        // a sensible floor when the env var is unset — which is ALWAYS the
        // case in headless and desktop Chrome, so this resolves to 0.75rem
        // there and no screenshot can evidence this line (BRIEF AC2).
        "pb-[max(0.75rem,env(safe-area-inset-bottom))]",
        className,
      )}
    >
      {/*
        Reading order matches the header bar's convention: corrective /
        destructive first, forward action rightmost.
      */}
      {actions.reject_url && (
        <Button
          type="button"
          variant="outline"
          data-testid="candidate-detail-sticky-reject"
          className="min-h-11 flex-1"
          disabled={disabled}
          aria-disabled={disabled}
          onClick={onRejectClick}
        >
          <X className="size-4" aria-hidden="true" />
          {t("candidates.detail.actions.reject")}
        </Button>
      )}
      {actions.approve_url && (
        <Button
          type="button"
          data-testid="candidate-detail-sticky-approve"
          className="min-h-11 flex-1"
          disabled={disabled}
          aria-disabled={disabled}
          aria-busy={approveInFlight}
          onClick={handleApprove}
        >
          {approveInFlight ? (
            <Spinner className="size-4" />
          ) : (
            <Check className="size-4" aria-hidden="true" />
          )}
          {t("candidates.detail.actions.approve")}
        </Button>
      )}
    </div>
  )
}
